'use client';

import { c as _c } from "react/compiler-runtime";
import { getDateDraws } from '@svs/nova-poolbetting/core/frameworks/actions/date-picker';
import type { DateDrawsResultDate } from '@svs/nova-poolbetting/schema/date-picker';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import { Calendar as ReactCalendar } from '@svs/ui/react/calendar';
import { Card } from '@svs/ui/react/card';
import Calendar from '@svs/ui/react/icons/Calendar';
import { Input } from '@svs/ui/react/input';
import { Popover, PopoverContent, PopoverTrigger } from '@svs/ui/react/popover';
import { format } from 'date-fns';
import { usePathname, useRouter } from 'next/navigation';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { getSportInfoRouteFromDrawState } from '@/components/index.browser';
import { getDateRangeForCurrentMonth, locale, prettyDate } from '../utils/helpers';
type DatePickerProps = {
  product: Definition['drawProducts'][number];
  selectedDate?: Date;
};

/**
 * Component för date picker.
 *
 * @param root0 - The props object.
 * @param root0.product - The product identifier for fetching date picker data.
 * @param root0.selectedDate - The selected date for the date picker.
 * @returns date picker component
 */
function DatePicker(t0) {
  const $ = _c(38);
  const {
    product,
    selectedDate
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [datesWithDraws, setDatesWithDraws] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [disabledDates, setDisabledDates] = useState(t2);
  const [specifiedDate, setSpecifiedDate] = useState();
  const [specifiedMonth, setSpecifiedMonth] = useState();
  let t3;
  if ($[2] !== selectedDate) {
    t3 = selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined;
    $[2] = selectedDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedDateKey = t3;
  const lastDate = useRef(selectedDateKey);
  const dateOnly = _temp;
  let t4;
  if ($[4] !== product) {
    t4 = async (month_0, year_0) => {
      const dateResultResponse = await getDateDraws({
        product,
        year: year_0,
        month: month_0
      });
      if (dateResultResponse.status === "SUCCESS" && dateResultResponse.data) {
        const {
          results,
          latestDate
        } = dateResultResponse.data;
        setDatesWithDraws(results);
        return {
          validDates: Object.keys(results).map(dateString_0 => dateOnly(dateString_0)),
          latestDate: latestDate ? dateOnly(latestDate) : undefined
        };
      }
      if (dateResultResponse.status === "ERROR") {}
      setDatesWithDraws({});
      return {
        validDates: [],
        latestDate: undefined
      };
    };
    $[4] = product;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const fetchValidDates = t4;
  let t5;
  if ($[6] !== fetchValidDates) {
    t5 = async date => {
      const year_1 = date.getFullYear();
      const month_1 = date.getMonth() + 1;
      const {
        validDates,
        latestDate: latestDate_0
      } = await fetchValidDates(month_1, year_1);
      const range = getDateRangeForCurrentMonth(date);
      return {
        dates: range.filter(dateInRange => !validDates.some(validDate => validDate.getMonth() === date.getMonth() && validDate.getTime() === dateInRange.getTime())),
        latestDate: latestDate_0
      };
    };
    $[6] = fetchValidDates;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const resolveDates = t5;
  let t6;
  if ($[8] !== datesWithDraws || $[9] !== pathname || $[10] !== router) {
    t6 = date_0 => {
      const dateStr = format(date_0, "yyyy-MM-dd");
      if (dateStr === lastDate.current) {
        return;
      }
      const draws = datesWithDraws[dateStr];
      const [draw] = draws ?? [];
      const sportInfoRoute = getSportInfoRouteFromDrawState(draw?.drawState.client);
      const nextPath = `${basePath}/resultat/${dateStr}${sportInfoRoute}`;
      if (pathname === nextPath) {
        lastDate.current = dateStr;
        return;
      }
      lastDate.current = dateStr;
      router.push(nextPath);
    };
    $[8] = datesWithDraws;
    $[9] = pathname;
    $[10] = router;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onDateSelect = t6;
  let t7;
  if ($[12] !== resolveDates) {
    t7 = async newDate => {
      setSpecifiedMonth(newDate);
      const {
        dates
      } = await resolveDates(newDate);
      setDisabledDates(dates);
    };
    $[12] = resolveDates;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onMonthChange = t7;
  let t8;
  let t9;
  if ($[14] !== selectedDateKey) {
    t8 = () => {
      lastDate.current = selectedDateKey;
    };
    t9 = [selectedDateKey];
    $[14] = selectedDateKey;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== resolveDates || $[18] !== selectedDateKey) {
    t10 = () => {
      const fetchDates = async () => {
        const stableSelectedDate = selectedDateKey ? dateOnly(selectedDateKey) : undefined;
        const {
          dates: dates_0,
          latestDate: latestDate_1
        } = await resolveDates(stableSelectedDate ?? new Date());
        setDisabledDates(dates_0);
        if (!stableSelectedDate && latestDate_1) {
          setSpecifiedDate(latestDate_1);
          setSpecifiedMonth(latestDate_1);
        } else {
          setSpecifiedDate(stableSelectedDate);
          setSpecifiedMonth(stableSelectedDate);
        }
      };
      fetchDates();
    };
    t11 = [resolveDates, selectedDateKey];
    $[17] = resolveDates;
    $[18] = selectedDateKey;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Calendar className="cursor-pointer" />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== specifiedDate) {
    t13 = specifiedDate ? prettyDate(specifiedDate) : "...";
    $[22] = specifiedDate;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <PopoverTrigger asChild={true}><div><Input className="cursor-pointer" data-testid="datePickerInput" type="text" placeholder={"V\xE4lj datum"} suffix={t12} readOnly={true} value={t13} /></div></PopoverTrigger>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = disabledDates.length > 0 ? disabledDates : true;
  let t16;
  if ($[26] !== onMonthChange) {
    t16 = newDate_0 => void onMonthChange(newDate_0);
    $[26] = onMonthChange;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== onDateSelect || $[29] !== specifiedDate || $[30] !== specifiedMonth || $[31] !== t15 || $[32] !== t16) {
    t17 = <PopoverContent className="border-stroke-medium w-auto rounded"><ReactCalendar data-testid="datePickerCalendar" disabled={t15} locale={locale} mode="single" month={specifiedMonth} onMonthChange={t16} onSelect={onDateSelect} required={true} showWeekNumber={true} selected={specifiedDate} /></PopoverContent>;
    $[28] = onDateSelect;
    $[29] = specifiedDate;
    $[30] = specifiedMonth;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== isOpen || $[35] !== t14 || $[36] !== t17) {
    t18 = <Card className="p-0"><Popover open={isOpen} onOpenChange={setIsOpen}>{t14}{t17}</Popover></Card>;
    $[34] = isOpen;
    $[35] = t14;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}
function _temp(dateString) {
  const datePart = dateString.slice(0, 10);
  const [yearStr, monthStr, dayStr] = datePart.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  return new Date(year, month - 1, day);
}
export default DatePicker;